import type {ThreadMessageLike} from '@assistant-ui/core';

import {isAcceptedMediaType} from '../components/WBAgent/attachments/imageParts';
import type {
  ContentPart as StoreContentPart,
  MessageNode,
} from './store/thread';

/**
 * wandb-specific store parts (mentions, calls, etc.) are encoded as AUI
 * `data` parts under this name. `data` is part of AUI's `ThreadUserMessagePart`
 * union, so the structured payload survives a round-trip through AUI's
 * `content` field without having to use `ThreadUserMessage.metadata`.
 */
export const WANDB_DATA_PART_NAME = 'wandb';

/**
 * toAUIMessage converts a store `MessageNode` to a `ThreadMessageLike` for
 * `assistant-ui`.
 */
export function toAUIMessage(node: MessageNode): ThreadMessageLike {
  const sourceParts =
    node.role === 'user' ? getVisiblePromptParts(node.content) : node.content;

  const content: AUIContentPart[] = [];
  for (const part of sourceParts) {
    content.push(...toAUIContentParts(part));
  }

  return {
    id: node.id,
    role: node.role,
    content,
    createdAt: new Date(node.createdAt),
  };
}

export function getVisiblePromptParts(
  parts: StoreContentPart[]
): StoreContentPart[] {
  // Drops "ambient" context parts (e.g., selected runs) that precede the
  // user's typed message.
  //
  // Relies on the invariant (set in `assemblePromptParts`) that the user's
  // typed message always begins with a text part. That text part is non-empty
  // if the user's prompt starts with text, and empty if it starts with a
  // mention - the empty text part is what lets us distinguish a leading mention
  // from the ambient selected-run context that precedes it, since the two are
  // otherwise structurally identical in our data model.
  const firstTextPartIndex = parts.findIndex(part => part.type === 'text');
  return firstTextPartIndex === -1 ? [] : parts.slice(firstTextPartIndex);
}

type AUIContentPart = Extract<
  ThreadMessageLike['content'],
  readonly unknown[]
>[number];

export function toAUIContentParts(part: StoreContentPart): AUIContentPart[] {
  switch (part.type) {
    case 'text':
      return [{type: 'text', text: part.text}];

    case 'thinking':
      return [{type: 'reasoning', text: part.text}];

    case 'tool-call':
      return [
        {
          type: 'tool-call',
          toolCallId: part.toolCallId,
          toolName: part.toolName,
          argsText: part.argsText,
          args: part.args ?? {},
          ...(part.result !== undefined ? {result: part.result} : {}),
          ...(part.isError !== undefined ? {isError: part.isError} : {}),
        },
      ];

    case 'image':
      return [
        {
          type: 'image',
          image: `data:${part.mediaType};base64,${part.base64Data}`,
        },
      ];

    case 'wandb-entity':
    case 'wandb-project':
    case 'wandb-run':
    case 'wandb-call':
    case 'wandb-conversation':
    case 'wandb-artifact-collection':
    case 'wandb-workspace-panel':
    case 'wandb-web-page-state':
      return [{type: 'data', name: WANDB_DATA_PART_NAME, data: part}];

    case 'unknown':
      return [];

    default:
      return [];
  }
}

/**
 * auiContentToStoreParts is the inverse of {@link toAUIContentParts}: it
 * decodes the AUI `content` array back into store `ContentPart`s, so the
 * runtime can rebuild a `BuiltPrompt` for the BE without consulting the
 * store-internal `MessageNode`s.
 */
export function auiContentToStoreParts(
  content: ReadonlyArray<{type: string; [key: string]: unknown}>
): StoreContentPart[] {
  const parts: StoreContentPart[] = [];
  for (const p of content) {
    if (p.type === 'text') {
      parts.push({
        type: 'text',
        text: typeof p.text === 'string' ? p.text : '',
        status: 'complete',
      });
    } else if (p.type === 'image' && typeof p.image === 'string') {
      const imagePart = dataUrlToImagePart(p.image);
      if (imagePart) {
        parts.push(imagePart);
      }
    } else if (
      p.type === 'data' &&
      (p as {name?: string}).name === WANDB_DATA_PART_NAME
    ) {
      parts.push((p as unknown as {data: StoreContentPart}).data);
    }
  }
  return parts;
}

const DATA_URL_RE = /^data:([^;,]+);base64,(.*)$/s;

/**
 * Parse a `data:<mime>;base64,<payload>` URL (the form {@link toAUIContentParts}
 * emits for images) back into a store `ImagePart`. Returns null for malformed
 * input so a bad part is dropped rather than corrupting the prompt.
 */
function dataUrlToImagePart(
  dataUrl: string
): Extract<StoreContentPart, {type: 'image'}> | null {
  const match = DATA_URL_RE.exec(dataUrl);
  if (!match) {
    return null;
  }
  const mediaType = match[1];
  if (!isAcceptedMediaType(mediaType)) {
    return null;
  }
  return {
    type: 'image',
    mediaType,
    base64Data: match[2],
  };
}
